<template>
  <v-container class="gp-admin">
    <div class="page-header">
      <h2>IABF {{ adminFestivalYear }} — Group pieces</h2>
    </div>
    <p class="muted">
      Dancers can join. Assign a faculty member. Rehearsal times go on the itinerary
      (faculty schedule + photoshoot conflicts).
    </p>

    <section class="gp-card">
      <h3>New piece</h3>
      <div class="gp-row">
        <label>Name
          <input v-model="draft.title" class="gp-input" placeholder="e.g. Waltz workshop">
        </label>
        <label>Faculty
          <select v-model="draft.facultyId" class="gp-input" @change="onDraftFaculty">
            <option value="">— pick faculty —</option>
            <option v-for="f in facultyEditors" :key="f.id" :value="f.id">{{ f.name }}</option>
          </select>
        </label>
        <v-btn small color="primary" :disabled="!draft.title || saving" :loading="saving" @click="createPiece">
          Add piece
        </v-btn>
      </div>
      <span v-if="flash" class="flash">{{ flash }}</span>
    </section>

    <section v-for="p in pieces" :key="p.id" class="gp-card">
      <div class="gp-head">
        <h3>{{ p.title }}</h3>
        <button type="button" class="linkish danger" @click="removePiece(p)">Delete</button>
      </div>
      <div class="gp-row">
        <label>Name
          <input :value="p.title" class="gp-input" @blur="onTitleBlur(p, $event)">
        </label>
        <label>Faculty
          <select class="gp-input" :value="p.facultyId || ''" @change="onFacultyChange(p, $event)">
            <option value="">— none —</option>
            <option v-for="f in facultyEditors" :key="f.id" :value="f.id">{{ f.name }}</option>
          </select>
        </label>
        <label class="gp-check">
          <input type="checkbox" :checked="p.openForRegistration !== false" @change="onOpenChange(p, $event)">
          Dancers can join
        </label>
      </div>

      <h4>Rehearsals at IABF</h4>
      <table class="gp-table" v-if="(p.rehearsals || []).length">
        <thead>
          <tr><th>Day</th><th>Time</th><th>Room</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="r in p.rehearsals" :key="r.id">
            <td>{{ dayLabel(r.day) }}</td>
            <td class="mono">{{ compactTimeLabel(r.startTime, r.endTime) }}</td>
            <td>{{ r.room || '—' }}</td>
            <td><button type="button" class="linkish danger" @click="removeRehearsal(p, r)">Remove</button></td>
          </tr>
        </tbody>
      </table>
      <p v-else class="hint">No rehearsal on the schedule yet.</p>
      <v-btn v-if="scheduleFor !== p.id" small color="primary" class="mt-2" @click="openSchedule(p)">
        + Schedule
      </v-btn>

      <div v-if="scheduleFor === p.id" class="schedule-wizard">
        <p class="wizard-step">1. Faculty</p>
        <label>Who’s teaching this rehearsal?
          <select v-model="scheduleFacultyId" class="gp-input" @change="onScheduleFaculty">
            <option value="">— pick faculty —</option>
            <option v-for="f in facultyEditors" :key="f.id" :value="f.id">{{ f.name }}</option>
          </select>
        </label>

        <template v-if="scheduleFacultyName">
          <p class="wizard-step">2. Times {{ scheduleFacultyName }} is not teaching</p>
          <p v-if="!itineraryClasses.length" class="hint">
            No itinerary yet — add classes on the itinerary page so we know the festival time grid.
          </p>
          <p v-else-if="!availableScheduleSlots.length" class="hint">
            No free itinerary slots for {{ scheduleFacultyName }}.
          </p>
          <div v-for="day in scheduleDays" :key="day.key" class="sched-day">
            <h5>{{ day.label }}</h5>
            <label v-for="s in day.slots" :key="s.key" class="sched-slot" :class="{ busy: !s.available, on: s.available && scheduleChecked[s.key] }">
              <input
                v-if="s.available"
                type="checkbox"
                :checked="!!scheduleChecked[s.key]"
                @change="toggleScheduleSlot(s, $event)"
              >
              <span v-else class="busy-mark">Busy</span>
              <span class="mono">{{ s.timeLabel }}</span>
              <span v-if="!s.available" class="busy-title">{{ s.busyTitle }}</span>
              <span v-else-if="slotAlreadyScheduled(p, s)" class="hint">already on this piece</span>
            </label>
          </div>
          <div class="gp-row">
            <label>Room for checked times
              <select v-model="scheduleRoom" class="gp-input">
                <option value="">—</option>
                <option v-for="room in rooms" :key="room" :value="room">{{ room }}</option>
              </select>
            </label>
            <v-btn
              small
              color="primary"
              :disabled="!checkedScheduleCount || scheduling"
              :loading="scheduling"
              @click="commitSchedule(p)"
            >Add {{ checkedScheduleCount }} to schedule</v-btn>
            <button type="button" class="linkish" @click="closeSchedule">Cancel</button>
          </div>
        </template>
      </div>

      <h4>Dancers ({{ (p.allDancerIds || []).length }})</h4>
      <ul class="gp-dancers">
        <li v-for="d in (p.dancers || [])" :key="d.id">
          {{ [d.firstName, d.lastName].filter(Boolean).join(' ') || d.email }}
          <button type="button" class="linkish danger" @click="removeDancer(p, d)">Remove</button>
        </li>
      </ul>
      <div class="gp-row">
        <label>Add dancer
          <input v-model="dancerQuery[p.id]" class="gp-input" list="gp-dancers" placeholder="Search name or email">
        </label>
        <v-btn small outlined @click="addDancerFromQuery(p)">Add</v-btn>
      </div>
    </section>
    <p v-if="!pieces.length" class="muted">No group pieces yet.</p>
    <datalist id="gp-dancers">
      <option v-for="d in dancerOptions" :key="d.id" :value="d.label" />
    </datalist>
  </v-container>
</template>

<script>
import { db } from '@/firebase'
import iabfAdminMixin from '@/utils/iabf-admin-mixin'
import {
  compactTimeLabel,
  emptyGroupPiece,
  facultyScheduleOptions,
  GROUP_PIECE_DAYS,
  GROUP_PIECE_ROOMS,
  itineraryClassFromGroupRehearsal,
  slotKey
} from '@/config/iabf-group-pieces'

export default {
  name: 'GroupPiecesAdmin',
  mixins: [iabfAdminMixin],
  data () {
    return {
      saving: false,
      flash: '',
      pieces: [],
      faculty: [],
      dancers: [],
      draft: emptyGroupPiece(),
      dancerQuery: {},
      itineraryClasses: [],
      scheduleFor: '',
      scheduleFacultyId: '',
      scheduleFacultyName: '',
      scheduleChecked: {},
      scheduleRoom: '',
      scheduling: false,
      days: GROUP_PIECE_DAYS,
      rooms: GROUP_PIECE_ROOMS
    }
  },
  computed: {
    facultyEditors () {
      return (this.faculty || []).map((f) => ({
        id: f.id,
        name: [f.firstName, f.lastName].filter(Boolean).join(' ').trim() || f.email,
        uid: f.uid || '',
        email: String(f.email || '').toLowerCase()
      })).filter((f) => f.name)
    },
    dancerOptions () {
      return (this.dancers || []).map((d) => ({
        id: d.id,
        label: [d.firstName, d.lastName].filter(Boolean).join(' ').trim() +
          (d.email ? ' <' + d.email + '>' : '')
      }))
    },
    scheduleOptions () {
      if (!this.scheduleFacultyName) {
        return []
      }
      return facultyScheduleOptions(this.itineraryClasses, this.scheduleFacultyName)
    },
    availableScheduleSlots () {
      return this.scheduleOptions.filter((s) => s.available)
    },
    scheduleDays () {
      return GROUP_PIECE_DAYS.map((d) => ({
        key: d.key,
        label: d.label,
        slots: this.scheduleOptions.filter((s) => s.day === d.key).map((s) => ({
          ...s,
          key: slotKey(s.day, s.startMin, s.endMin)
        }))
      })).filter((d) => d.slots.length)
    },
    checkedScheduleCount () {
      return Object.keys(this.scheduleChecked).filter((k) => this.scheduleChecked[k]).length
    }
  },
  watch: {
    adminFestivalYear () {
      this.reload()
    }
  },
  created () {
    this.reload()
  },
  methods: {
    compactTimeLabel,
    dayLabel (key) {
      const row = GROUP_PIECE_DAYS.find((d) => d.key === key)
      return row ? row.label : key
    },
    facultyFromId (id) {
      return this.facultyEditors.find((f) => f.id === id) || null
    },
    onDraftFaculty () {
      const f = this.facultyFromId(this.draft.facultyId)
      this.draft.facultyName = f ? f.name : ''
      this.draft.facultyEmail = f ? f.email : ''
      this.draft.facultyUid = f ? f.uid : ''
    },
    ensureDrafts (piece) {
      if (this.dancerQuery[piece.id] == null) {
        this.$set(this.dancerQuery, piece.id, '')
      }
    },
    async reload () {
      try {
        const cols = this.IABF
        const [pieces, faculty, classSnap] = await Promise.all([
          this.$store.dispatch('loadGroupPieces', { useAdminYear: true }),
          this.$store.dispatch('loadIabfFaculty'),
          cols && cols.classes ? db.collection(cols.classes).get() : Promise.resolve({ docs: [] })
        ])
        this.pieces = pieces || []
        this.faculty = faculty || []
        this.itineraryClasses = (classSnap.docs || []).map((doc) => ({ id: doc.id, ...doc.data() }))
        this.pieces.forEach((p) => this.ensureDrafts(p))
        await this.$store.dispatch('loadIABFDancers')
        this.dancers = this.$store.getters.loadedIABFDancers || []
      } catch (e) {
        console.warn('group pieces admin load failed', e)
      }
    },
    async persist (piece) {
      const saved = await this.$store.dispatch('saveGroupPiece', piece)
      Object.assign(piece, saved)
      await this.syncItinerary(piece)
      return saved
    },
    async syncItinerary (piece) {
      const cols = this.IABF
      if (!cols || !cols.classes) {
        return
      }
      const snap = await db.collection(cols.classes).where('groupPieceId', '==', piece.id).get()
      const keep = {}
      const rehearsals = piece.rehearsals || []
      for (let i = 0; i < rehearsals.length; i++) {
        const cls = itineraryClassFromGroupRehearsal(piece, rehearsals[i])
        keep[cls.id] = true
        const { id, ...payload } = cls
        await db.collection(cols.classes).doc(id).set(payload, { merge: true })
      }
      const batch = db.batch()
      let n = 0
      snap.docs.forEach((doc) => {
        if (!keep[doc.id]) {
          batch.delete(doc.ref)
          n += 1
        }
      })
      if (n) {
        await batch.commit()
      }
    },
    async createPiece () {
      this.saving = true
      this.flash = ''
      try {
        this.onDraftFaculty()
        const saved = await this.$store.dispatch('saveGroupPiece', this.draft)
        this.pieces = this.pieces.concat([saved]).sort((a, b) => String(a.title).localeCompare(String(b.title)))
        this.ensureDrafts(saved)
        this.draft = emptyGroupPiece()
        this.flash = 'Added'
        setTimeout(() => { this.flash = '' }, 2000)
      } catch (e) {
        this.flash = (e && e.message) || 'Could not add'
      }
      this.saving = false
    },
    async onTitleBlur (piece, ev) {
      const title = String(ev && ev.target ? ev.target.value : '').trim()
      if (!title || title === piece.title) {
        return
      }
      piece.title = title
      await this.persist(piece)
    },
    async onFacultyChange (piece, ev) {
      const f = this.facultyFromId(ev && ev.target ? ev.target.value : '')
      piece.facultyId = f ? f.id : ''
      piece.facultyName = f ? f.name : ''
      piece.facultyEmail = f ? f.email : ''
      piece.facultyUid = f ? f.uid : ''
      await this.persist(piece)
    },
    async onOpenChange (piece, ev) {
      piece.openForRegistration = !!(ev && ev.target && ev.target.checked)
      await this.persist(piece)
    },
    openSchedule (piece) {
      this.scheduleFor = piece.id
      this.scheduleFacultyId = piece.facultyId || ''
      this.scheduleFacultyName = piece.facultyName || ''
      this.scheduleChecked = {}
      this.scheduleRoom = ''
      if (!this.scheduleFacultyName && this.scheduleFacultyId) {
        const f = this.facultyFromId(this.scheduleFacultyId)
        this.scheduleFacultyName = f ? f.name : ''
      }
    },
    closeSchedule () {
      this.scheduleFor = ''
      this.scheduleFacultyId = ''
      this.scheduleFacultyName = ''
      this.scheduleChecked = {}
    },
    onScheduleFaculty () {
      const f = this.facultyFromId(this.scheduleFacultyId)
      this.scheduleFacultyName = f ? f.name : ''
      this.scheduleChecked = {}
    },
    slotAlreadyScheduled (piece, slot) {
      return (piece.rehearsals || []).some((r) =>
        r.day === slot.day && r.startTime === slot.startTime && r.endTime === slot.endTime
      )
    },
    toggleScheduleSlot (slot, ev) {
      const on = !!(ev && ev.target && ev.target.checked)
      this.$set(this.scheduleChecked, slot.key, on)
    },
    async commitSchedule (piece) {
      const picked = this.scheduleOptions
        .map((s) => ({ ...s, key: slotKey(s.day, s.startMin, s.endMin) }))
        .filter((s) => s.available && this.scheduleChecked[s.key])
      if (!picked.length) {
        return
      }
      const f = this.facultyFromId(this.scheduleFacultyId)
      if (f) {
        piece.facultyId = f.id
        piece.facultyName = f.name
        piece.facultyEmail = f.email
        piece.facultyUid = f.uid
      }
      const existing = piece.rehearsals || []
      const next = existing.slice()
      picked.forEach((s) => {
        if (this.slotAlreadyScheduled(piece, s)) {
          return
        }
        next.push({
          id: [s.day, s.startMin, s.endMin].join('-'),
          day: s.day,
          startTime: s.startTime,
          endTime: s.endTime,
          room: this.scheduleRoom || ''
        })
      })
      this.scheduling = true
      try {
        piece.rehearsals = next
        await this.persist(piece)
        this.closeSchedule()
      } catch (e) {
        window.alert((e && e.message) || 'Could not add those times')
      }
      this.scheduling = false
    },
    async removeRehearsal (piece, row) {
      piece.rehearsals = (piece.rehearsals || []).filter((r) => r.id !== row.id)
      await this.persist(piece)
    },
    async addDancerFromQuery (piece) {
      const q = String(this.dancerQuery[piece.id] || '').trim().toLowerCase()
      if (!q) {
        return
      }
      const match = (this.dancers || []).find((d) => {
        const name = [d.firstName, d.lastName].filter(Boolean).join(' ').toLowerCase()
        const email = String(d.email || '').toLowerCase()
        const label = name + (email ? ' <' + email + '>' : '')
        return label === q || email === q || name === q || (email && q.indexOf(email) !== -1)
      })
      if (!match) {
        window.alert('No dancer matched that name.')
        return
      }
      const ids = piece.allDancerIds || []
      if (ids.indexOf(match.id) !== -1) {
        return
      }
      piece.allDancerIds = ids.concat([match.id])
      piece.dancers = (piece.dancers || []).concat([{
        id: match.id,
        firstName: match.firstName || '',
        lastName: match.lastName || '',
        email: String(match.email || '').toLowerCase()
      }])
      this.dancerQuery[piece.id] = ''
      await this.persist(piece)
    },
    async removeDancer (piece, dancer) {
      piece.allDancerIds = (piece.allDancerIds || []).filter((id) => id !== dancer.id)
      piece.dancers = (piece.dancers || []).filter((d) => d.id !== dancer.id)
      await this.persist(piece)
    },
    async removePiece (piece) {
      if (!window.confirm('Delete “' + piece.title + '” and its itinerary rehearsals?')) {
        return
      }
      piece.rehearsals = []
      await this.syncItinerary(piece)
      await this.$store.dispatch('deleteGroupPiece', piece.id)
      this.pieces = this.pieces.filter((p) => p.id !== piece.id)
    }
  }
}
</script>

<style scoped>
.gp-admin { max-width: 980px; }
.page-header h2 {
  font-family: Quattrocento, Georgia, serif;
  font-weight: 400;
  font-size: 32px;
  margin: 0 0 8px;
}
.muted { color: #6b6570; font-size: 15px; }
.hint { font-size: 13px; color: #8a8792; }
.gp-card {
  background: #fff;
  border: 1px solid #eceae6;
  border-radius: 12px;
  padding: 16px 18px 18px;
  margin: 16px 0;
}
.gp-card h3 { margin: 0 0 12px; font-size: 18px; }
.gp-card h4 { margin: 16px 0 8px; font-size: 14px; }
.gp-head { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; }
.gp-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  align-items: flex-end;
}
.gp-row label { display: flex; flex-direction: column; gap: 4px; font-size: 13px; font-weight: 600; }
.gp-input {
  font: inherit;
  font-weight: 400;
  padding: 6px 8px;
  border: 1px solid #d8d4ce;
  border-radius: 6px;
  min-width: 140px;
}
.gp-check { flex-direction: row !important; align-items: center; gap: 8px !important; }
.gp-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.gp-table th, .gp-table td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #eee; }
.mono { font-variant-numeric: tabular-nums; }
.gp-dancers { list-style: none; padding: 0; margin: 0 0 8px; }
.gp-dancers li { padding: 4px 0; font-size: 14px; }
.linkish {
  border: none;
  background: none;
  color: #267E7D;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  text-decoration: underline;
}
.linkish.danger { color: #b54a4a; }
.flash { margin-left: 12px; font-size: 13px; color: #2f6b4f; font-weight: 600; }
.schedule-wizard {
  margin-top: 12px;
  padding: 12px 14px;
  border: 1px dashed #d8d4ce;
  border-radius: 10px;
  background: #faf9f7;
}
.wizard-step { font-weight: 700; margin: 10px 0 8px; font-size: 14px; }
.sched-day { margin: 10px 0; }
.sched-day h5 { margin: 0 0 6px; font-size: 14px; }
.sched-slot {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 400;
}
.sched-slot.on { background: #eef6f1; }
.sched-slot.busy { color: #8a8792; }
.busy-mark { font-size: 11px; font-weight: 700; text-transform: uppercase; color: #b54a4a; min-width: 36px; }
.busy-title { color: #8a8792; font-size: 13px; }
</style>
